'use client';

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaSearchengin } from "react-icons/fa6";

import AnimatedLogo from "../../components/AnimatedLogo";
import ProjectCard from "../../components/Projects/ProjectCard";
import { IoClose } from "react-icons/io5";
import { useGetProjects, useGetProjectTags } from "../../api/projects";
import { projects2 as dummyProjects } from "./data";

// On localhost, fall back to dummy portfolio data when the API has none.
const isLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

// ProjectCard/Tags expect tags as { id, tag } objects; dummy data uses strings.
const normalizedDummyProjects = dummyProjects.map((project) => ({
  ...project,
  tags: (project.tags || []).map((tag, index) => ({
    id: `${project.id}-${index}`,
    tag,
  })),
}));

function Projects() {
  const [pageSize, setPageSize] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("sort_order");

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const selectedTagIds = searchParams.get("tags")?.split(",").map(Number) || [];

  const setSelectedTags = (tagId) => {
    let updatedTags = [];

    if (tagId === null) {
      updatedTags = []; // Reset to "All"
    } else {
      updatedTags = selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((id) => id !== tagId) // Remove if exists
        : [...selectedTagIds, tagId]; // Add if not exists
    }

    const query = updatedTags.length ? `?tags=${updatedTags.join(",")}` : "";
    router.replace(`${pathname}${query}`, { scroll: false });
  };

  const tagsForApi = selectedTagIds.length ? selectedTagIds.join(",") : "";

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    error,
  } = useGetProjects(pageSize, debouncedSearchTerm, order, orderBy, tagsForApi);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reference to last element for intersection observer
  const observer = useRef();

  const lastElementRef = useCallback(
    (node) => {
      if (isFetching || !hasNextPage) return; // Prevent fetching if already loading

      if (observer.current) observer.current.disconnect(); // Disconnect previous observer

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage(); // Fetch next page when last item enters viewport
        }
      });

      if (node) observer.current.observe(node); // Attach observer to the new last element
    },
    [isFetching, hasNextPage, fetchNextPage]
  );

  const apiProjects = data?.pages.flatMap((page) => page.data) || [];

  // Use dummy data locally when the backend returns no projects.
  const projects =
    apiProjects.length === 0 && isLocalhost && !isLoading
      ? normalizedDummyProjects
      : apiProjects;

  return (
    <div>
      <h1 className="text-color-1 text-center text-4xl lg:text-6xl font-medium leading-[normal] my-5 md:my-8">
        Portfolio
      </h1>
      {/* Search & Tags */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-5 mb-10">
        <div className="w-72 md:w-80">
          <SearchInput search={searchTerm} setSearch={setSearchTerm} />
        </div>
        <Tags
          selectedTagIds={selectedTagIds}
          setSelectedTags={setSelectedTags}
        />
      </div>

      {/* Projects List */}
      {isLoading ? (
        <div className="flex flex-wrap justify-center gap-7 animate-pulse">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="w-[302px] md:w-[392px] h-[216px] md:h-[262px] rounded-xl bg-zinc-900"
            />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center gap-4 text-xl md:text-2xl mt-10">
          <AnimatedLogo />
          <div>
            <span className="text-color-2">No results? </span>
            <Link href="/contact-us" className="underline text-color-1">
              Let's create one!
            </Link>
            💡
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center gap-7">
          {projects.map((project, index) => {
            const isLast = index === projects.length - 1;
            return (
              <ProjectCard
                key={project.id}
                to={`/projects/${project.slug}`}
                project={project}
                selectedTagIds={selectedTagIds}
                setSelectedTags={setSelectedTags}
                ref={isLast ? lastElementRef : null} // Attach observer to last element
              />
            );
          })}
          {/* Loading more indicator */}
          {isFetchingNextPage &&
            [...Array(2)].map((_, i) => (
              <div
                key={i}
                className="w-[302px] md:w-[392px] h-[216px] md:h-[262px] rounded-xl bg-zinc-900"
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default Projects;

function SearchInput({ search, setSearch }) {
  return (
    <div className="relative w-full">
      <FaSearchengin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-color-2" />
      <input
        type="text"
        placeholder="Search projects..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className={`text-sm peer w-full bg-color-98 text-color-1 border rounded-lg px-10 py-2 transition duration-300 ease focus:outline-none focus:border-[var(--accent-dark)] hover:border-[var(--accent-dark)] shadow-sm focus:shadow border-color-97`}
      />

      {search && (
        <button
          onClick={() => setSearch("")}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-color-2 hover:text-color-1 transition"
        >
          <IoClose className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function Tags({ selectedTagIds, setSelectedTags }) {
  const { data, isLoading, isFetching, error } = useGetProjectTags();

  const scrollRef = useRef(null);

  const handleMouseDown = (e) => {
    const slider = scrollRef.current;
    let startX = e.pageX - slider.offsetLeft;
    let scrollLeft = slider.scrollLeft;

    const handleMouseMove = (e) => {
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2; // Adjust speed
      slider.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };
  return (
    <div
      ref={scrollRef}
      className="px-5 w-full md:w-fit flex gap-2 overflow-x-auto scrollbar-hide whitespace-nowrap"
      onMouseDown={handleMouseDown}
    >
      <button
        key={null}
        className={`rounded-full `}
        onClick={() => setSelectedTags(null)}
      >
        <div
          className={`relative overflow-hidden flex justify-center items-center gap-2 py-1.5 px-4 rounded-full border text-sm text-center font-medium transition-all duration-300 ${
            selectedTagIds.includes(null) ||
            (null === null && !selectedTagIds.length)
              ? " border-color-97 text-color-100"
              : "border-color-97 hover:border-color-50 text-color-2 hover:text-color-1"
          }`}
        >
          <div
            className={`absolute h-full w-full bg-[linear-gradient(180deg,_var(--accent-light),_var(--accent-dark))] transition-all duration-500 ${
              selectedTagIds.includes(null) ||
              (null === null && !selectedTagIds.length)
                ? " opacity-100"
                : "opacity-0"
            } `}
          ></div>
          <p className="z-10">All</p>
        </div>
      </button>

      {isLoading
        ? [...Array(5)].map((_, i) => (
            <button className={`rounded-full `} key={i}>
              <div
                className={`relative h-8 w-20 overflow-hidden flex justify-center items-center gap-2 py-1.5 px-4 rounded-full border text-sm text-center font-medium transition-all duration-300 bg-zinc-900 animate-pulse border-color-97 text-color-100`}
              ></div>
            </button>
          ))
        : (() => {
            const desiredOrder = ["Websites", "Mobile Apps", "Social Media Marketing", "AI", "OTT", "CRM"];
            const apiTags = data?.tagsData || [];
            
            // Hardcoded mapping as a safety net if DB is not synced
            const fallbackIds = {
                "Websites": 10,
                "Mobile Apps": 11,
                "Social Media Marketing": 12,
                "AI": 7,
                "OTT": 13,
                "CRM": 2
            };

            // Ensure every desired tag is represented
            const finalTags = desiredOrder.map(tagName => {
                const found = apiTags.find(t => 
                    t.tag.trim().toLowerCase() === tagName.toLowerCase()
                );
                return found || { id: fallbackIds[tagName], tag: tagName };
            });

            return finalTags.map((tag) => (
                <button
                  key={tag.id}
                  className={`rounded-full `}
                  onClick={() => setSelectedTags(tag.id)}
                >
                  <div
                    className={`relative overflow-hidden flex justify-center items-center gap-2 py-1.5 px-4 rounded-full border text-sm text-center font-medium transition-all duration-300 ${
                      selectedTagIds.includes(tag.id)
                        ? " border-color-97 text-color-100"
                        : "border-color-97 hover:border-color-50 text-color-2 hover:text-color-1"
                    }`}
                  >
                    <div
                      className={`absolute h-full w-full bg-[linear-gradient(180deg,_var(--accent-light),_var(--accent-dark))] transition-all duration-500 ${
                        selectedTagIds.includes(tag.id)
                          ? " opacity-100"
                          : "opacity-0"
                      } `}
                    ></div>
                    <p className="z-10">{tag.tag}</p>
                  </div>
                </button>
            ));
          })()}
    </div>
  );
}
